import type { BoxProps } from '@chakra-ui/react';
import { Box, IconButton } from '@chakra-ui/react';
import { Xmark } from 'iconoir-react';

interface DrawerContextBarProps extends BoxProps {
  /** Dismisses the drawer. The bar owns the gesture, never the URL. */
  onClose: () => void;
  /** Accessible name of the close affordance, already translated. */
  closeLabel: string;
}

/**
 * Minimum height of the bar, in px. Content stuck right under it (a section nav)
 * offsets itself by this value.
 */
export const DRAWER_CONTEXT_BAR_MIN_HEIGHT = 60;

/**
 * The canonical sticky bar atop a side drawer: one 60px row carrying whatever
 * names the screen, with the round close X pinned to its right edge and
 * vertically centred on the row however tall the row grows.
 *
 * Extracted from the contact drawer's context bar so a second drawer cannot
 * drift from it: the geometry (minimum height, paddings, hairline, blur) and
 * the close button's placement live here once. Translucent backdrop + bottom
 * hairline, no shadow.
 */
export const DrawerContextBar: React.FC<DrawerContextBarProps> = ({
  onClose,
  closeLabel,
  children,
  ...rest
}) => (
  <Box
    position="sticky"
    top="0"
    zIndex="1"
    bg="panelBg/92"
    backdropFilter="blur(8px)"
    borderBottomWidth="1px"
    borderColor="border.subtle"
    minH={`${DRAWER_CONTEXT_BAR_MIN_HEIGHT}px`}
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    gap="3"
    pl="22px"
    pr="64px"
    py="13px"
    {...rest}
  >
    {children}

    <IconButton
      aria-label={closeLabel}
      onClick={onClose}
      variant="outline"
      size="sm"
      rounded="full"
      boxSize="34px"
      minW="34px"
      position="absolute"
      top="50%"
      transform="translateY(-50%)"
      insetEnd="22px"
      zIndex="2"
      color="primary.400"
      borderColor="border"
      bg="transparent"
      _hover={{ bg: 'whiteSmoke', color: 'dark' }}
    >
      <Xmark />
    </IconButton>
  </Box>
);
